import * as React from 'react';
import { Button, Card, Collapse, Row, Col, Tooltip } from 'antd';

import { DataLocalService } from 'src/core/services/data-local.service';
import { DataTableView, ToolbarVisibility, Column } from 'src/core/ui/data-table.view';
import { ObjectUtils } from 'src/core/utils/object';
import { SupplementList } from './search-results-supplement.view';
import { RoomSummaryView } from './search-results-room-summary.view';

//imports de cosas que puede que no vayan aki
import { ProductDataService } from 'src/app/products/products/form/product-form.data';
import { ProductCategoryListDataService } from 'src/app/products/product-categories/list/product-category-list.data';


interface RoomListProp {
	parent: ProductSearchResult;
	items: RoomSearchResult[];
	setBusy: (isBusy: boolean) => void;
	selection: { [unitIndex: string]: ResultSelection };
	onChangeSelection: (selectedRowKeys: string[] | number[], selectedRows: Object[]) => any;
	onChangeOptionalSelection: (selectedRowKeys: string[] | number[], selectedRows: Object[]) => any;
	taxEditor?: React.ReactNode;
}

type ViewType = 'supplements' | 'info';

export const RoomList: React.SFC<RoomListProp> = (props) => {
	const { items, onChangeSelection, onChangeOptionalSelection, selection, setBusy, taxEditor, children } = props;

	const maxRoom: number = getMaxRoom(items);

	let roomsData: ResultOption[][] = []
	for (let i = 1; i <= maxRoom; i++) {
		roomsData[i] = [];
	}
	const mappedData = mapToResultOption(props);
	for (let i = 0; i < mappedData.length; i++) {
		const row = mappedData[i];
		roomsData[row.unitIndex] = roomsData[row.unitIndex].concat(row);
	}
	const selectionKeys = [];
	if (selection != null) {
		for (const prop of Object.getOwnPropertyNames(selection)) {
			const unitIndex = parseInt(prop);
			const id = selection[unitIndex].selected.id;
			if (mappedData.some(p => p.id === id)) {
				selectionKeys.push(`${unitIndex}-${id}`);
			}
		}
	}

	const rooms: any[] = [];
	let supplements = [[]];
	const defaultActiveKey = [];
	for (let i = 1; i <= maxRoom; i++) {
		supplements.push([]);
		for (const item of items) {
			for (const subProd of item.subProducts) {
				const cost = subProd.costPrice[i];
				const sell = subProd.sellPrice[i];
				if (cost && sell) {
					supplements[i].push({
						id: subProd.id,
						unitIndex: i,
						cost: cost,
						sell: sell,
						result: subProd,
						parent: item,
					});
				}
			}
		}
		const { minPrice, minPriceCurrency, minPriceProduct } = getMinPrice(roomsData[i]);
		const header =
			<Row type="flex" justify="space-between">
				<Col span={4}><span style={{ fontWeight: "bold" }}>{`Room ${i}`}</span></Col>
				<Col span={2}>
					{/* <Badge count={rooms.length > 1 ? rooms.length : 0} offset={[0, 5]}> */}
					<Tooltip title={minPriceProduct}>
						{minPrice} {minPriceCurrency}
					</Tooltip>
					{/* </Badge> */}
				</Col>
			</Row>

		const detailsVisible = (selection && selection[i] && roomsData[i] && roomsData[i].some((room) => {
			return selection[i].selected.id === room.id;
		}));

		const details = <Col span={10}>
			<DetailsAndSupplementsContainer
				parent={items[0]}
				items={supplements[i]}
				setBusy={setBusy}
				onChangeSelection={onChangeOptionalSelection}
				selection={selection}
				unitTitle="Room"
				unitIndex={i}
				parentRooms={roomsData[i]}
				taxEditor={taxEditor}
			>
				{children}
			</DetailsAndSupplementsContainer>
		</Col>

		rooms.push(
			<Collapse.Panel key={`${i}`} header={header}>
				<Row type="flex" justify="space-between">
					<Col span={detailsVisible ? 14 : 24}>
						<DataTableView
							rowKey={getSupDiscKey}
							mode="display"
							setBusy={setBusy}
							hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
							columns={getColumnsPro(props)}
							model={new DataLocalService(roomsData[i] as any, 40)}
							selection={
								{
									type: "radio",
									selectedRowKeys: selectionKeys,
									onChange: onChangeSelection,
								}
							}
						/>
					</Col>
					{detailsVisible && details}
				</Row>
			</Collapse.Panel>
		);
		defaultActiveKey.push(i.toString());
	}
	return (
		<Collapse defaultActiveKey={defaultActiveKey}>
			{rooms}
		</Collapse>
	);
}

const getKey = (record: ProductSearchResult) => `${record.id}`;
const getSupDiscKey = (record: ResultOption) => `${record.unitIndex}-${record.id}`;

const getColumnsPro = (props: RoomListProp) => {
	const { items, onChangeSelection, onChangeOptionalSelection, selection, setBusy, parent } = props;

	const columns: Column[] = [
		{
			key: 'source',
			dataIndex: 'result.productProviderName',
			title: 'Source',
			render: (text, record: ResultOption, index: number) => {
				return {
					children: text
				}
			}
		},
		{
			key: 'name',
			dataIndex: 'result.product.name',
			title: 'Name',
			render: (text, record: ResultOption, index: number) => {
				return {
					children: text
				}
			}
		},
		{
			key: 'supplier',
			dataIndex: 'result.supplier.title',
			title: 'Supplier',
			render: (text, record: ResultOption, index: number) => {
				return {
					children: text
				}
			}
		},
		{
			key: 'features',
			dataIndex: 'result.features',
			title: 'Features',
			render: (text: AccommodationCategoryMap) => {
				return text ? `${text.code}` : '';
			}
		},
		{
			key: 'price1',
			dataIndex: '',
			title: 'Price',
			render: (text, record: ResultOption) => {
				return calculateTotalPrice(record, selection);
			}
		},
		{
			key: 'cost',
			dataIndex: 'cost',
			title: 'Margin',
			render: (text: Money, record: ResultOption) => {
				return calculateTotalMargin(record, selection);
			},
			width: '6rem'
		},
	]
	return columns;
}
const calculateTotalPrice = (record: ResultOption, selection: { [unitIndex: string]: ResultSelection }) => {
	const price = record.sell.totalPrice;
	const selectedOptions = selection && selection[record.unitIndex]
		? selection[record.unitIndex].supDisc.filter(e => !(e.result as SupplementSearchResult).isRequired && e.parent.id == record.id)
		: [];
	const totalSellOptions = selectedOptions.reduce((prev, curr) => prev + curr.sell.totalPrice, 0);
	return `${ObjectUtils.round(price + totalSellOptions)} ${record.sell.currencyIsoCode}`;
}
const calculatePriceWithoutRequired = (record: ResultOption, selection: { [unitIndex: string]: ResultSelection }) => {
	const price = record.sell.totalPrice;
	const selectedOptions = selection && selection[record.unitIndex]
		? selection[record.unitIndex].supDisc.filter(e => (e.result as SupplementSearchResult).isRequired)
		: [];
	const totalSellOptions = selectedOptions.reduce((prev, curr) => prev + curr.sell.totalPrice, 0);
	return `${ObjectUtils.round(price - totalSellOptions)} ${record.sell.currencyIsoCode}`;
}
const calculateTotalMargin = (record: ResultOption, selection: { [unitIndex: string]: ResultSelection }) => {
	const margin = record.sell.totalPrice - (record.cost.totalPrice * record.sell.exchangeRate);
	const selectedOptions = selection && selection[record.unitIndex]
		? selection[record.unitIndex].supDisc.filter(e => !(e.result as SupplementSearchResult).isRequired && e.parent.id == record.id)
		: [];
	const totalSellOptions = selectedOptions.reduce((prev, curr) => prev + curr.sell.totalPrice - (curr.cost.totalPrice * curr.sell.exchangeRate), 0);
	return `${ObjectUtils.round(margin + totalSellOptions)} ${record.sell.currencyIsoCode}`;
}
const calculateMarginWithoutRequired = (record: ResultOption, selection: { [unitIndex: string]: ResultSelection }) => {
	const margin = record.sell.totalPrice - (record.cost.totalPrice * record.sell.exchangeRate);
	const selectedOptions = selection && selection[record.unitIndex]
		? selection[record.unitIndex].supDisc.filter(e => (e.result as SupplementSearchResult).isRequired)
		: [];
	const totalSellOptions = selectedOptions.reduce((prev, curr) => prev + curr.sell.totalPrice - (curr.cost.totalPrice * curr.sell.exchangeRate), 0);
	return `${ObjectUtils.round(margin - totalSellOptions)} ${record.sell.currencyIsoCode}`;
}

const mapToResultOption = (props: RoomListProp) => {
	const { items, parent } = props;
	let mappedData: ResultOption[] = [];
	for (let i = 0; i < items.length; i++) {
		const item = items[i];
		for (const prop of Object.getOwnPropertyNames(item.costPrice)) {
			const unitIndex = parseInt(prop);
			const cost = item.costPrice[unitIndex];
			const sell = item.sellPrice[unitIndex];
			mappedData.push(
				{
					id: item.id,
					unitIndex: unitIndex,
					cost: cost,
					sell: sell,
					result: item,
					parent: parent,
				}
			);
		}
	}
	return mappedData;
}
const getMaxRoom = (items: ProductSearchResult[]) => {
	return items.map((value) => {
		let maxRoom = 0;
		for (const prop of Object.getOwnPropertyNames(value.costPrice)) {
			const unitIndex = parseInt(prop);
			maxRoom = Math.max(maxRoom, unitIndex);
		}
		return maxRoom;
	}).reduce((prev, curr) => {
		return Math.max(prev, curr);
	});
}
const getMinPrice = (rooms: ResultOption[]) => {
	let minPrice: number;
	let minPriceCurrency: string;
	let minPriceProduct: string;
	for (const room of rooms) {
		const price = room.sell;
		if (minPrice === undefined || minPrice > price.totalPrice) {
			minPrice = price.totalPrice;
			minPriceProduct = `${room.result.product.name} (${(room.result as RoomSearchResult).features.code})`;
			minPriceCurrency = price.currencyIsoCode;
		}
	}
	return {
		minPrice: minPrice,
		minPriceCurrency: minPriceCurrency,
		minPriceProduct: minPriceProduct
	}
}

const spanRows = (text: string, field: string, items: ProductSearchResult[], index: number) => {
	const span = items.filter(e => aggregateFromField(e, field) === text).length;
	const prevIsDiff = index === 0 || aggregateFromField(items[index - 1], field) !== text;
	return prevIsDiff ? span : 0
}

const aggregateFromField = (item: ProductSearchResult, field: string) => {
	const fields = field.split(".");
	let result: any = item;
	for (const f of fields) {
		result = result[f];
	}
	return result;
}

interface DetailsAndSupplementsContainerProps {
	parent: RoomSearchResult;
	items: ResultOption[];
	setBusy: (isBusy: boolean) => void;
	onChangeSelection: (selectedRowsKeys: number[] | string[], selectedRows: Object[]) => any;
	selection: { [unitIndex: string]: ResultSelection };
	unitTitle: string;
	unitIndex: number;
	parentRooms: ResultOption[];
	taxEditor?: React.ReactNode;
}

interface DetailsAndSupplementsContainerState {
	activeView: string | null;
	loadedProduct: ProductItem;
}

class DetailsAndSupplementsContainer extends React.Component<DetailsAndSupplementsContainerProps, DetailsAndSupplementsContainerState>{
	productDataService: ProductDataService;
	categoryDataService: ProductCategoryListDataService;

	constructor(props: Readonly<DetailsAndSupplementsContainerProps>) {
		super(props);
		this.state = {
			activeView: "supplements",
			loadedProduct: null,
		};
		this.productDataService = ProductDataService.make();
		this.categoryDataService = ProductCategoryListDataService.make();
	}

	public render(): React.ReactNode {
		const { parent, items, setBusy, onChangeSelection, selection, unitTitle, unitIndex, parentRooms, taxEditor } = this.props;
		const { activeView, loadedProduct } = this.state;

		const tabs =
			[
				<Button
					key="supplements"
					onClick={() => this.handleChangeView('supplements')}
					{...this.getButtonPropsForActiveView('supplements')}
				>
					Supplements
				</Button>,
				<Button
					key="info"
					onClick={() => this.handleChangeView('info')}
					{...this.getButtonPropsForActiveView('info')}
				>
					Room Info
				</Button>
			];
		const supplements = <SupplementList
			parent={parent}
			items={items}
			setBusy={setBusy}
			onChangeSelection={onChangeSelection}
			selection={selection}
			unitTitle={unitTitle}
			unitIndex={unitIndex}
		/>;

		const info = <RoomSummaryView
			setBusy={setBusy}
			product={loadedProduct}
			picturesWidth={300}
			textWidth={400}
			externalPadding={30}
			internalPadding={15}
			colSpan={10}
		/>

		const anyOneSelected = selection && selection[unitIndex] && parentRooms && parentRooms.some((room) => {
			return selection[unitIndex].selected.id === room.id;
		});
		const totalPrice = anyOneSelected ? calculateTotalPrice(selection[unitIndex].selected, selection) : 0;
		const totalMargin = anyOneSelected ? calculateTotalMargin(selection[unitIndex].selected, selection) : 0;

		const priceWithoutRequired = anyOneSelected ? calculatePriceWithoutRequired(selection[unitIndex].selected, selection) : 0;
		const marginWithoutRequired = anyOneSelected ? calculateMarginWithoutRequired(selection[unitIndex].selected, selection) : 0;

		return (
			<div className="card card-borderless ml-2">
				<Col>
					<Row type="flex" justify="space-between">
						<Col>{tabs[0]}</Col>
						<Col>{tabs[1]}</Col>
					</Row>
					<Card>
						<Row>
							{activeView === 'supplements' && supplements}
							{activeView === 'info' && info}
						</Row>
					</Card>
					<Card>
						<Row align="bottom" type="flex">
							<Col offset={1} span={10}><span style={{ fontWeight: "bold" }}>Room Price</span></Col>
							<Col span={6}>{priceWithoutRequired}</Col>
							<Col span={6}>{marginWithoutRequired}</Col>
						</Row>
					</Card>
					<Card>
						<Row align="bottom" type="flex">
							<Col offset={1} span={10}><span style={{ fontWeight: "bold" }}>Total</span></Col>
							<Col span={6}>{totalPrice}</Col>
							<Col span={6}>{totalMargin}</Col>
						</Row>
						<Row className="mt-2" type="flex" justify="space-between">
							<Col xs={24} lg={{ span: 8 }} className="ml-1">{this.props.taxEditor}</Col>
							<Col xs={24} lg={{ span: 8 }} style={{ textAlign: "right" }} className="mr-1">{this.props.children}</Col>
						</Row>
					</Card>
				</Col>
			</div>

		);
	}

	private handleChangeView = async (viewName: ViewType) => {
		const { parentRooms, unitIndex, selection } = this.props;
		this.setState({
			activeView: viewName
		});
		if (selection) {
			let internalId = null
			for (const room of parentRooms) {
				if (selection[unitIndex].selected.id === room.id)
					internalId = room.result.product.internalId
			}
			if (viewName === "info" && this.state.loadedProduct == null && internalId) {
				this.productDataService.addFilter("id", "eq", internalId);
				this.productDataService.addSorting("id", "asc");
				this.productDataService.pageSize = 1;
				await this.productDataService.load(query => query);

				const product = this.productDataService.item;

				this.categoryDataService.addFilter('id', 'eq', product.categoryId);
				this.categoryDataService.addSorting('id', 'asc', true);
				this.categoryDataService.pageSize = 1;
				await this.categoryDataService.load(query => query);

				product.category = this.categoryDataService.item;

				this.setState({
					loadedProduct: product
				});
			}
		}
	}

	private getButtonPropsForActiveView(viewName: ViewType): any {
		const { activeView } = this.state;
		const { items, selection, parentRooms, unitIndex } = this.props;

		let disabledSupplement = true;
		let disabledInfo = true;
		if (selection) {
			for (const item of items) {
				if (selection[unitIndex] && item.parent.id === selection[unitIndex].selected.id)
					disabledSupplement = false;
			}
			disabledInfo = !(selection[unitIndex] && parentRooms && parentRooms.some((room) => {
				return selection[unitIndex].selected.id === room.id;
			}));
		}

		return {
			type: activeView === viewName ? 'primary' : undefined,
			disabled: viewName === "supplements" ? disabledSupplement : disabledInfo
		} as any;
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/search/results/search-results-room.view.tsx